import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto 60px;
  padding: 30px 20px;
  background-color: ${props => props.theme.card};
  border-radius: 10px;
  box-shadow: ${props => props.theme.shadow};
  margin-top: 30px;
  margin-left: 85px;
  
  @media (max-width: 768px) {
    padding: 20px 15px;
    margin-top: 20px;
    margin-bottom: 80px;
    margin-left: 0;
  }
`;

const Title = styled.h1`
  color: ${props => props.theme.text};
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Section = styled.section`
  margin-bottom: 30px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.primary};
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 15px;
  border-bottom: 1px solid ${props => props.theme.border};
  padding-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Paragraph = styled.p`
  color: ${props => props.theme.text};
  margin-bottom: 15px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const List = styled.ul`
  margin-bottom: 15px;
  padding-left: 25px;
  
  li {
    color: ${props => props.theme.text};
    margin-bottom: 10px;
    line-height: 1.5;
    
    @media (max-width: 768px) {
      font-size: 15px;
    }
  }
`;

const BoldText = styled.strong`
  font-weight: 600;
  color: ${props => props.theme.textStrong};
`;

const LastUpdated = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
  font-style: italic;
  margin-top: 30px;
  text-align: center;
`;

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Términos y Condiciones | Sapotix</title>
        <meta name="description" content="Términos y condiciones de Sapotix, la red social anónima para estudiantes universitarios." />
      </Helmet>

      <Container>
        <Title>Términos y Condiciones de Sapotix</Title>
        
        <Section>
          <Paragraph>
            Bienvenido a Sapotix, la plataforma de comunicación anónima para estudiantes universitarios. Antes de utilizar nuestros servicios, le pedimos que lea detenidamente estos términos y condiciones. Al acceder o utilizar Sapotix, usted acepta estar legalmente vinculado por los siguientes términos y políticas.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>1. Naturaleza del Servicio</SectionTitle>
          <Paragraph>
            Sapotix es una plataforma que permite a los estudiantes universitarios compartir contenido de forma anónima dentro de sus respectivas comunidades educativas. Nuestro objetivo es fomentar la libre expresión en un entorno controlado, destinado exclusivamente a la comunidad universitaria verificada.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>2. Descargo de Responsabilidad</SectionTitle>
          <Paragraph>
            <BoldText>Sapotix actúa exclusivamente como intermediario tecnológico neutral</BoldText> que facilita la comunicación entre usuarios, sin intervenir en el contenido compartido. No creamos, editamos, filtramos previamente ni monitoreamos el contenido publicado por los usuarios, salvo por el filtrado automático de imágenes mediante tecnología de AWS Rekognition, diseñado únicamente para detectar contenido sexual explícito en imágenes.
          </Paragraph>
          <Paragraph>
            <BoldText>Todo el contenido publicado en Sapotix refleja única y exclusivamente las opiniones de los usuarios individuales</BoldText> que lo comparten y bajo ninguna circunstancia representa las opiniones, creencias o posiciones de Sapotix, sus propietarios, operadores, empleados o afiliados. Sapotix no respalda ni avala ningún contenido compartido por sus usuarios.
          </Paragraph>
          <Paragraph>
            <BoldText>Cada usuario es el único y absoluto responsable del contenido que comparte</BoldText>, incluyendo cualquier opinión, comentario, crítica o información que pueda ser considerada difamatoria, ofensiva o inadecuada por terceros.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>3. Contenido Prohibido</SectionTitle>
          <Paragraph>
            Si bien Sapotix promueve la libre expresión dentro del ámbito universitario, los usuarios son legalmente responsables por el contenido que publican. La siguiente lista constituye una guía sobre contenido potencialmente problemático, pero se proporciona únicamente con fines informativos:
          </Paragraph>
          <List>
            <li>Contenido que viole leyes nacionales, estatales o locales aplicables</li>
            <li>Amenazas directas y específicas de violencia física contra individuos identificables</li>
            <li>Contenido sexual explícito en imágenes (filtrado por AWS Rekognition)</li>
            <li>Contenido que infrinja derechos de propiedad intelectual claramente establecidos</li>
          </List>
          <Paragraph>
            <BoldText>Sapotix no supervisa activamente el contenido más allá del filtrado automático de imágenes explícitas.</BoldText> Los usuarios utilizan la plataforma bajo su propio riesgo y criterio.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>4. Limitación de Responsabilidad</SectionTitle>
          <Paragraph>
            <BoldText>Sapotix renuncia completamente a cualquier responsabilidad legal por el contenido publicado por sus usuarios.</BoldText> Como proveedor de servicios de intermediación y alojamiento de contenido, Sapotix está protegido por las leyes aplicables que limitan la responsabilidad de plataformas por el contenido generado por usuarios, incluyendo pero no limitado a la Sección 230 de la Ley de Decencia en las Comunicaciones (para usuarios de EE.UU.) y leyes similares en otras jurisdicciones.
          </Paragraph>
          <Paragraph>
            <BoldText>En ningún caso Sapotix, sus operadores, propietarios, empleados o afiliados serán responsables de:</BoldText>
          </Paragraph>
          <List>
            <li>Cualquier contenido publicado, compartido o transmitido por los usuarios a través de la plataforma</li>
            <li>Cualquier afirmación difamatoria, inexacta, ofensiva o ilegal realizada por usuarios</li>
            <li>Cualquier daño directo, indirecto, incidental, especial, consecuencial o punitivo derivado del uso de la plataforma o del contenido en ella compartido</li>
            <li>Cualquier pérdida de reputación, oportunidades o beneficios resultantes del contenido compartido en Sapotix</li>
          </List>
          <Paragraph>
            Sapotix solo retirará contenido específico cuando:
          </Paragraph>
          <List>
            <li>Sea requerido mediante orden judicial válida y ejecutable</li>
            <li>Constituya una amenaza directa e inmediata de daño físico verificable</li>
            <li>Sea detectado automáticamente como contenido sexual explícito en imágenes por AWS Rekognition</li>
          </List>
        </Section>
        
        <Section>
          <SectionTitle>5. Moderación de Contenido</SectionTitle>
          <Paragraph>
            <BoldText>La moderación de contenido en Sapotix es extremadamente limitada y se restringe a:</BoldText>
          </Paragraph>
          <List>
            <li>Filtrado automático de imágenes mediante AWS Rekognition para detectar contenido sexual explícito</li>
            <li>Actuación únicamente en casos de requisitos legales ineludibles o amenazas directas verificables</li>
          </List>
          <Paragraph>
            <BoldText>Sapotix no emplea moderadores humanos que revisen proactivamente el contenido compartido.</BoldText> Se espera que los usuarios ejerzan su propio criterio y sean responsables de sus interacciones en la plataforma.
          </Paragraph>
          <Paragraph>
            <BoldText>Al utilizar Sapotix, usted reconoce y acepta expresamente que el contenido no está moderado</BoldText> y que la plataforma no es responsable de verificar la exactitud, legalidad o moralidad del contenido compartido por otros usuarios.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>6. Indemnización</SectionTitle>
          <Paragraph>
            <BoldText>Al utilizar Sapotix, usted acepta de manera irrevocable e incondicional indemnizar, defender y eximir completamente de responsabilidad</BoldText> a Sapotix, sus propietarios, operadores, empleados, afiliados, socios, agentes y licenciantes frente a cualquier y todo reclamo, daño, obligación, pérdida, responsabilidad, costo, deuda, multa, gasto, incluyendo pero no limitado a honorarios legales razonables y costos, derivados de:
          </Paragraph>
          <List>
            <li>Su uso de la plataforma bajo cualquier circunstancia</li>
            <li>Cualquier contenido que usted publique, comparta o transmita a través de Sapotix</li>
            <li>Cualquier interacción con otros usuarios de la plataforma</li>
            <li>Cualquier violación de estos términos o de derechos de terceros</li>
            <li>Cualquier actividad relacionada con su cuenta, sea realizada por usted o por cualquier tercero, con o sin su autorización</li>
            <li>Cualquier demanda, proceso o acción legal iniciada por terceros como resultado de su conducta o contenido compartido</li>
          </List>
          <Paragraph>
            <BoldText>Esta obligación de indemnización sobrevivirá a la terminación de su cuenta y/o su uso de Sapotix.</BoldText>
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>7. Rectificación y Disputas de Contenido</SectionTitle>
          <Paragraph>
            <BoldText>Sapotix no arbitra disputas entre usuarios ni hace determinaciones sobre la veracidad de las afirmaciones o contenido compartido.</BoldText> Si considera que algún contenido publicado es inexacto o perjudicial:
          </Paragraph>
          <List>
            <li>Tiene la libertad de publicar su propia versión o respuesta usando la plataforma</li>
            <li>Debe dirigir cualquier reclamo legal directamente al usuario que publicó el contenido</li>
            <li>Puede solicitar la identidad del usuario mediante proceso legal formal dirigido a Sapotix</li>
          </List>
          <Paragraph>
            <BoldText>Sapotix solo responderá a órdenes judiciales válidas y ejecutables</BoldText> emitidas por tribunales con jurisdicción competente en México. Las solicitudes de eliminación de contenido basadas solamente en desacuerdos, opiniones contrarias o alegaciones no verificadas no serán procesadas.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>8. Privacidad y Anonimato</SectionTitle>
          <Paragraph>
            Sapotix ofrece un servicio que permite a los usuarios publicar de forma anónima ante otros usuarios, pero:
          </Paragraph>
          <List>
            <li><BoldText>Mantenemos registros internos vinculados a su identidad verificada</BoldText> que pueden ser revelados en respuesta a órdenes judiciales válidas</li>
            <li>El anonimato ofrecido es frente a otros usuarios, no frente a requerimientos legales válidos</li>
            <li>Cada usuario es legalmente responsable de su contenido, independientemente del anonimato percibido</li>
          </List>
          <Paragraph>
            <BoldText>Al usar Sapotix, usted reconoce explícitamente que su anonimato es limitado</BoldText> y que puede ser identificado legalmente como el autor de cualquier contenido que publique si así lo requiere una autoridad competente mediante procedimiento legal adecuado.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>9. Uso Académico</SectionTitle>
          <Paragraph>
            Sapotix es una plataforma exclusiva para comunidades universitarias verificadas. Como tal:
          </Paragraph>
          <List>
            <li>Se requiere verificación mediante correo electrónico institucional para acceder a ciertas funcionalidades</li>
            <li>Los usuarios que intenten falsificar su afiliación universitaria serán expulsados</li>
            <li>Cada universidad constituye una comunidad separada con contenido principalmente visible para miembros de esa institución</li>
          </List>
        </Section>
        
        <Section>
          <SectionTitle>10. Modificaciones a los Términos</SectionTitle>
          <Paragraph>
            Sapotix se reserva el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en la plataforma. El uso continuado de Sapotix después de cualquier modificación constituye aceptación de los términos actualizados.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>11. Ley Aplicable</SectionTitle>
          <Paragraph>
            Estos términos se regirán e interpretarán de acuerdo con las leyes de México, sin tener en cuenta sus disposiciones sobre conflictos de leyes. Cualquier disputa que surja de estos términos o en relación con ellos estará sujeta a la jurisdicción exclusiva de los tribunales de Chihuahua, México.
          </Paragraph>
          <Paragraph>
            <BoldText>Usted acepta expresamente que cualquier reclamación o disputa que pudiera tener contra Sapotix deberá presentarse exclusivamente ante los tribunales mencionados</BoldText> y renuncia a cualquier derecho a iniciar o participar en litigios colectivos contra Sapotix.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>12. Exención de Garantías</SectionTitle>
          <Paragraph>
            <BoldText>SAPOTIX SE PROPORCIONA "TAL CUAL" Y "SEGÚN DISPONIBILIDAD", SIN GARANTÍAS DE NINGÚN TIPO, YA SEAN EXPRESAS O IMPLÍCITAS.</BoldText> Sapotix renuncia expresamente a todas las garantías, ya sean expresas, implícitas o estatutarias, incluyendo, pero no limitado a, cualquier garantía implícita de comercialización, idoneidad para un propósito particular, título y no infracción.
          </Paragraph>
          <Paragraph>
            <BoldText>Sapotix no garantiza que:</BoldText>
          </Paragraph>
          <List>
            <li>El servicio cumplirá con sus requisitos específicos</li>
            <li>El servicio será ininterrumpido, oportuno, seguro o libre de errores</li>
            <li>Los resultados que puedan obtenerse del uso del servicio serán precisos o confiables</li>
            <li>La calidad de cualquier información, contenido o material obtenido a través del servicio satisfará sus expectativas</li>
            <li>Cualquier error en el servicio será corregido</li>
          </List>
          <Paragraph>
            <BoldText>CUALQUIER MATERIAL DESCARGADO U OBTENIDO A TRAVÉS DEL USO DEL SERVICIO SE UTILIZA BAJO SU PROPIO CRITERIO Y RIESGO.</BoldText> Usted será el único responsable de cualquier daño a su sistema informático o pérdida de datos que resulte de la descarga de dicho material.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>13. Acuerdo Completo</SectionTitle>
          <Paragraph>
            Estos términos constituyen el acuerdo completo entre usted y Sapotix con respecto al uso de la plataforma, y reemplazan todas las comunicaciones, propuestas y representaciones anteriores o contemporáneas, ya sean electrónicas, orales o escritas, entre usted y Sapotix con respecto a la plataforma.
          </Paragraph>
          <Paragraph>
            <BoldText>Al marcar la casilla de aceptación durante el registro o al continuar utilizando Sapotix, usted reconoce que ha leído, entendido y acepta estar legalmente vinculado por estos términos en su totalidad.</BoldText> Si no está de acuerdo con alguna parte de estos términos, debe cesar inmediatamente todo uso de la plataforma.
          </Paragraph>
        </Section>
        
        <LastUpdated>Última actualización: 6 de abril de 2025</LastUpdated>
      </Container>
    </>
  );
};

export default Terms; 